import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TablePagination,
  TextField,
  InputAdornment,
  Stack,
  Tooltip,
  IconButton,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp,
  TrendingDown,
  EmojiEvents as TrophyIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
  Sort as SortIcon,
  KeyboardArrowUp,
  KeyboardArrowDown
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import api from '../../../services/api';
import logger from '../../../utils/logger';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3)
}));

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  borderRadius: '16px',
  border: '1px solid rgba(0,0,0,0.05)'
}));

const StatCard = styled(Card)(({ theme, bgcolor }) => ({
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  borderRadius: '16px',
  background: bgcolor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100px',
    height: '100px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '50%',
    transform: 'translate(30%, -30%)'
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '12px',
  '& .MuiTableHead-root': {
    backgroundColor: '#f8fafc'
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 600,
    color: '#475569',
    borderBottom: '2px solid #e2e8f0',
    padding: '16px'
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f1f5f9'
    }
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    padding: '14px 16px',
    borderBottom: '1px solid #f1f5f9'
  }
}));

const RankBadge = styled(Box)(({ rank }) => {
  let bgColor = '#64748b';
  let textColor = '#fff';

  if (rank === 1) {
    bgColor = 'linear-gradient(135deg, #ffd700 0%, #ffb347 100%)';
  } else if (rank === 2) {
    bgColor = 'linear-gradient(135deg, #c0c0c0 0%, #a8a8a8 100%)';
  } else if (rank === 3) {
    bgColor = 'linear-gradient(135deg, #cd7f32 0%, #b87333 100%)';
  } else if (rank <= 5) {
    bgColor = 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)';
  } else if (rank <= 10) {
    bgColor = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
  } else if (rank <= 20) {
    bgColor = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
  } else {
    bgColor = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
  }

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '40px',
    height: '32px',
    borderRadius: '8px',
    background: bgColor,
    color: textColor,
    fontWeight: 700,
    fontSize: '14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
  };
});

const ScoreBar = styled(Box)(({ score }) => {
  const percentage = Math.min((score / 100) * 100, 100);
  let color = '#ef4444';

  if (score >= 80) color = '#22c55e';
  else if (score >= 60) color = '#3b82f6';
  else if (score >= 40) color = '#f59e0b';

  return {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    '& .score-value': {
      fontWeight: 600,
      minWidth: '45px',
      color: color
    },
    '& .score-bar': {
      flex: 1,
      height: '8px',
      backgroundColor: '#e2e8f0',
      borderRadius: '4px',
      overflow: 'hidden',
      '& .score-fill': {
        height: '100%',
        width: `${percentage}%`,
        backgroundColor: color,
        borderRadius: '4px',
        transition: 'width 0.5s ease'
      }
    }
  };
});

const SearchInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0b91d6'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0b91d6'
    }
  }
}));

const CompetitorScore = () => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('analysis_date');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [statistics, setStatistics] = useState({
    averageRank: 0,
    averageScore: 0,
    uniqueUsers: 0,
    uniqueBusinesses: 0
  });
  const [searchDebounce, setSearchDebounce] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchCompetitorScores = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page + 1,
        pageSize: rowsPerPage,
        search: searchDebounce,
        sortBy,
        sortOrder
      }).toString();

      const response = await api.get(`/competitor-analysis/admin/all-scores?${queryParams}`);

      logger.log('[CompetitorScore] Response:', response);

      if (response?.data?.success) {
        setScores(response.data.data || []);
        setTotalItems(response.data.pagination?.totalItems || 0);
        setStatistics(response.data.statistics || {});
      } else if (response?.data) {
        setScores(response.data || []);
        setTotalItems(response.pagination?.totalItems || 0);
        setStatistics(response.statistics || {});
      }
    } catch (error) {
      logger.error('[CompetitorScore] Error fetching scores:', error);
      setScores([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchDebounce, sortBy, sortOrder]);

  useEffect(() => {
    fetchCompetitorScores();
  }, [fetchCompetitorScores]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const SortableHeader = ({ column, label }) => (
    <TableCell
      onClick={() => handleSort(column)}
      sx={{ cursor: 'pointer', userSelect: 'none' }}
    >
      <Box display="flex" alignItems="center" gap={0.5}>
        <strong>{label}</strong>
        {sortBy === column && (
          sortOrder === 'ASC' ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />
        )}
      </Box>
    </TableCell>
  );

  return (
    <PageContainer>
      <Stack spacing={3}>
        {/* Page Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h4" fontWeight={700} color="#1e293b" gutterBottom>
              Competitor Score Analysis
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track and analyze competitor rankings and scores across all users
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={fetchCompetitorScores}
              sx={{
                backgroundColor: '#0b91d6',
                color: '#fff',
                '&:hover': { backgroundColor: '#0a7bb8' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard bgcolor="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Average Rank
                    </Typography>
                    <Typography variant="h4" fontWeight={700} mt={1}>
                      #{statistics.averageRank || '-'}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <TrophyIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard bgcolor="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Average Score
                    </Typography>
                    <Typography variant="h4" fontWeight={700} mt={1}>
                      {statistics.averageScore || '-'}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <AnalyticsIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard bgcolor="linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Unique Users
                    </Typography>
                    <Typography variant="h4" fontWeight={700} mt={1}>
                      {statistics.uniqueUsers || 0}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <PeopleIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard bgcolor="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Businesses
                    </Typography>
                    <Typography variant="h4" fontWeight={700} mt={1}>
                      {statistics.uniqueBusinesses || 0}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <BusinessIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>

        {/* Main Table Card */}
        <StyledCard>
          <CardContent>
            {/* Search and Filters */}
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
              <SearchInput
                placeholder="Search by business, keyword, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                size="small"
                sx={{ minWidth: 320, flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value="analysis_date">Date</MenuItem>
                  <MenuItem value="my_business_rank">Rank</MenuItem>
                  <MenuItem value="my_business_score">Score</MenuItem>
                  <MenuItem value="business_name">Business Name</MenuItem>
                  <MenuItem value="keyword">Keyword</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Order</InputLabel>
                <Select
                  value={sortOrder}
                  label="Order"
                  onChange={(e) => setSortOrder(e.target.value)}
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value="DESC">Newest First</MenuItem>
                  <MenuItem value="ASC">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Loading State */}
            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            {/* Data Table */}
            <StyledTableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <SortableHeader column="my_business_rank" label="Rank" />
                    <SortableHeader column="business_name" label="Business Name" />
                    <TableCell><strong>User</strong></TableCell>
                    <SortableHeader column="keyword" label="Keyword" />
                    <SortableHeader column="my_business_score" label="Score" />
                    <TableCell><strong>Competitors</strong></TableCell>
                    <TableCell><strong>Location</strong></TableCell>
                    <SortableHeader column="analysis_date" label="Analysis Date" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && scores.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Box py={6}>
                          <AnalyticsIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary">
                            {searchTerm ? 'No matching records found' : 'No competitor analysis data available'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm ? 'Try adjusting your search criteria' : 'Data will appear here once users run competitor analysis'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    scores.map((record, index) => {
                      const user = record.analysisUser || {};
                      const rank = record.my_business_rank || '-';
                      const score = parseFloat(record.my_business_score) || 0;

                      return (
                        <TableRow key={record.id || index}>
                          <TableCell>
                            <RankBadge rank={rank}>
                              {rank}
                            </RankBadge>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600} color="#1e293b">
                                {record.business_name || 'N/A'}
                              </Typography>
                              {record.location_id && (
                                <Typography variant="caption" color="text.secondary">
                                  ID: {record.location_id.slice(-8)}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: '#0b91d6',
                                  fontSize: '14px'
                                }}
                              >
                                {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {user.full_name || 'Unknown'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {user.email || ''}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={record.keyword || 'N/A'}
                              size="small"
                              sx={{
                                backgroundColor: '#e0f2fe',
                                color: '#0369a1',
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            <ScoreBar score={score}>
                              <span className="score-value">{score.toFixed(1)}</span>
                              <div className="score-bar">
                                <div className="score-fill" />
                              </div>
                            </ScoreBar>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${record.total_competitors || 0} found`}
                              size="small"
                              variant="outlined"
                              sx={{ borderRadius: '6px' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={record.search_location || 'N/A'}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  maxWidth: 150,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {record.search_location || 'N/A'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(record.analysis_date)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              sx={{
                borderTop: '1px solid #f1f5f9',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontWeight: 500
                }
              }}
            />
          </CardContent>
        </StyledCard>
      </Stack>
    </PageContainer>
  );
};

export default CompetitorScore;
